import React, { useEffect, useRef, useState } from 'react';
import { Meeting } from '../../../../types/meeting';
import liveAPI from '../../../../services/liveApi';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface MeetingChatPanelProps {
  meeting: Meeting;
  /** Renders a smaller embedded variant for use alongside other panels. */
  compact?: boolean;
  /** Allows the panel to be collapsed via its header (used on multi-card pages). */
  collapsible?: boolean;
  /**
   * Controlled conversation. When provided, the panel renders these messages
   * and reports changes via onMessagesChange instead of holding them locally.
   * Lifting this state to the workspace keeps the Advisor history alive across
   * tab switches (which unmount the panel) and shares it between panel instances.
   */
  messages?: ChatMessage[];
  onMessagesChange?: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
}

export const extractHistory = (meeting: Meeting): ChatMessage[] => {
  const messages: ChatMessage[] = [];
  for (const entry of meeting.conversation_history || []) {
    for (const msg of entry.chat_messages || []) {
      if (msg.message_type === 'user' || msg.message_type === 'assistant') {
        messages.push({ role: msg.message_type, content: msg.content });
      }
    }
  }
  return messages;
};

const MeetingChatPanel: React.FC<MeetingChatPanelProps> = ({
  meeting,
  compact = false,
  collapsible = false,
  messages: controlledMessages,
  onMessagesChange,
}) => {
  // Local fallback used only when the panel is uncontrolled (no messages prop).
  const [localMessages, setLocalMessages] = useState<ChatMessage[]>(() => extractHistory(meeting));
  const messages = controlledMessages ?? localMessages;
  const setMessages = onMessagesChange ?? setLocalMessages;
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const isCollapsed = collapsible && collapsed;
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isSending]);

  const send = async () => {
    const query = input.trim();
    if (!query || isSending) return;

    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: query }]);
    setIsSending(true);

    // Stream the advisor response (contracts.md §8.5); liveAPI.streamChat falls
    // back to the synchronous /chat endpoint when the stream endpoint is absent.
    let assistantIndex = -1;
    setMessages((prev) => {
      assistantIndex = prev.length;
      return [...prev, { role: 'assistant', content: '' }];
    });

    try {
      await liveAPI.streamChat(meeting.meeting_id, query, (delta) => {
        setMessages((prev) => {
          const next = [...prev];
          const target = next[assistantIndex];
          if (target && target.role === 'assistant') {
            next[assistantIndex] = { role: 'assistant', content: target.content + delta };
          }
          return next;
        });
      });
    } catch (err) {
      setMessages((prev) => {
        const next = [...prev];
        if (next[assistantIndex]?.role === 'assistant') {
          next[assistantIndex] = {
            role: 'assistant',
            content: `Sorry, something went wrong: ${err instanceof Error ? err.message : 'unknown error'}`
          };
        }
        return next;
      });
    } finally {
      setIsSending(false);
    }
  };

  const hasTranscript = Boolean(meeting.transcript_text);

  return (
    <div className={`meeting-panel meeting-chat${compact ? ' meeting-chat-compact' : ''}${isCollapsed ? ' meeting-chat-collapsed' : ''}`}>
      <div
        className={`meeting-panel-header${collapsible ? ' meeting-panel-header-collapsible' : ''}`}
        role={collapsible ? 'button' : undefined}
        aria-expanded={collapsible ? !collapsed : undefined}
        onClick={collapsible ? () => setCollapsed((c) => !c) : undefined}
      >
        <div>
          <h3 className="meeting-panel-title">Advisor</h3>
          <p className="meeting-panel-subtitle">
            {hasTranscript
              ? 'Ask about what was said, decided, or assigned — or anything in the materials'
              : 'Ask about the materials, agenda, attendees, or how to prepare'}
          </p>
        </div>
        {collapsible && (
          <span className={`meeting-panel-chevron ${collapsed ? '' : 'expanded'}`}>▾</span>
        )}
      </div>

      {!isCollapsed && (
      <div className="meeting-chat-messages">
        {messages.length === 0 && (
          <div className="meeting-empty-hint">
            {hasTranscript
              ? 'Try: "What did we decide about the budget?" or "Who owns the follow-up actions?"'
              : 'Try: "What are the key risks in the uploaded documents?" or "What pushback should I expect?"'}
          </div>
        )}
        {messages.map((msg, idx) => (
          <div key={idx} className={`meeting-chat-msg ${msg.role}`}>{msg.content}</div>
        ))}
        {isSending && (
          <div className="meeting-chat-msg assistant">
            <div className="meeting-generating" style={{ padding: 0 }}>
              <span className="spinner" /> Thinking...
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      )}

      {!isCollapsed && (
      <div className="meeting-chat-input-row">
        <input
          className="meeting-chat-input"
          type="text"
          placeholder="Ask the advisor..."
          value={input}
          disabled={isSending}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
        />
        <button
          className="meeting-btn meeting-btn-primary"
          onClick={send}
          disabled={!input.trim() || isSending}
        >
          Send
        </button>
      </div>
      )}
    </div>
  );
};

export default MeetingChatPanel;
